import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Calculate as CalculateIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import PayrollService, { Payroll } from '../../services/payroll.service';
import EmployeeService from '../../services/employee.service';
import { NumberTextField } from '../../components/common';

const PayrollForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);

  const [formData, setFormData] = useState<Payroll>({
    employee: 0,
    period: new Date().toISOString().slice(0, 7), // YYYY-MM
    baseSalary: 0,
    bonuses: 0,
    deductions: 0,
    overtime: 0,
    tax: 0,
    netSalary: 0,
    currency: 'OMR',
    status: 'draft',
    paymentMethod: 'bank_transfer',
    notes: '',
    workDays: 22,
  });

  useEffect(() => {
    fetchEmployees();
    if (isEdit && id) {
      fetchPayroll(parseInt(id));
    }
  }, [id, isEdit]);

  // Auto-calculate net salary when any component changes
  useEffect(() => {
    calculateNetSalary();
  }, [
    formData.baseSalary,
    formData.bonuses,
    formData.deductions,
    formData.overtime,
    formData.tax,
  ]);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await EmployeeService.getAll({
        'pagination[pageSize]': 100,
        'filters[status][$eq]': 'active',
        populate: 'department',
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchPayroll = async (payrollId: number) => {
    try {
      setLoading(true);
      const response = await PayrollService.getOne(payrollId);
      const data = response.data?.data;

      if (data) {
        const attrs = data.attributes;
        // Strapi expects yyyy-MM-dd for date fields; API may return ISO string
        const paymentDateRaw = attrs.paymentDate || '';
        const paymentDate = paymentDateRaw ? String(paymentDateRaw).split('T')[0] : '';
        setFormData({
          employee: attrs.employee?.data?.id || 0,
          period: attrs.period || '',
          baseSalary: attrs.baseSalary || 0,
          bonuses: attrs.bonuses || 0,
          deductions: attrs.deductions || 0,
          overtime: attrs.overtime || 0,
          tax: attrs.tax || 0,
          netSalary: attrs.netSalary || 0,
          currency: attrs.currency || 'USD',
          status: attrs.status || 'draft',
          paymentDate,
          paymentMethod: attrs.paymentMethod || 'bank_transfer',
          notes: attrs.notes || '',
          workDays: attrs.workDays || 22,
        });

        // Set selected employee for autocomplete
        if (attrs.employee?.data) {
          setSelectedEmployee(attrs.employee.data);
        }
      }
    } catch (error: any) {
      console.error('Error fetching payroll:', error);
      setError('Failed to load payroll record');
    } finally {
      setLoading(false);
    }
  };

  const calculateNetSalary = () => {
    setFormData((prev) => {
      const base = Number(prev.baseSalary) || 0;
      const bonuses = Number(prev.bonuses) || 0;
      const deductions = Number(prev.deductions) || 0;
      const overtime = Number(prev.overtime) || 0;
      const tax = Number(prev.tax) || 0;
      const gross = base + bonuses + overtime;
      const net = gross - deductions - tax;
      return { ...prev, netSalary: Math.max(0, net) };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /** Explicit handler for number fields so state always updates correctly (does not rely on e.target.name) */
  const handleNumberFieldChange = (field: keyof Payroll) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmployeeChange = (event: any, newValue: any) => {
    setSelectedEmployee(newValue);
    const employeeId = newValue?.id || 0;
    const employeeAttrs = newValue?.attributes || {};
    
    // Auto-fill fields from employee data
    const employeeSalary = employeeAttrs.salary || 0;
    const employeeCurrency = employeeAttrs.currency || 'OMR';
    const salaryFrequency = employeeAttrs.salaryFrequency || 'M';
    
    // Default work days based on salary frequency (Monthly = 22, Weekly = 5, etc.)
    let defaultWorkDays = 22;
    if (salaryFrequency === 'W') defaultWorkDays = 5;
    else if (salaryFrequency === 'B') defaultWorkDays = 10;
    else if (salaryFrequency === 'D') defaultWorkDays = 1;
    
    setFormData((prev) => ({
      ...prev,
      employee: employeeId,
      baseSalary: employeeSalary,
      currency: employeeCurrency,
      workDays: defaultWorkDays,
    }));
  };

  const handleCalculateTax = () => {
    setFormData((prev) => {
      // Always read from prev so we have the latest state (avoids stale closure)
      const base = Number(prev.baseSalary) || 0;
      const bonuses = Number(prev.bonuses) || 0;
      const overtime = Number(prev.overtime) || 0;
      const gross = base + bonuses + overtime;

      let tax = 0;
      if (gross <= 5000) {
        tax = gross * 0.05; // 5%
      } else if (gross <= 10000) {
        tax = 250 + (gross - 5000) * 0.1; // 10%
      } else if (gross <= 20000) {
        tax = 750 + (gross - 10000) * 0.15; // 15%
      } else {
        tax = 2250 + (gross - 20000) * 0.2; // 20%
      }

      const roundedTax = Math.round(tax * 100) / 100;
      return { ...prev, tax: roundedTax };
    });
  };

  const validateForm = (): boolean => {
    if (!formData.employee) {
      setError('Please select an employee');
      return false;
    }
    if (!formData.period) {
      setError('Please select a period');
      return false;
    }
    if (!formData.baseSalary || formData.baseSalary <= 0) {
      setError('Please enter a valid base salary');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Strapi expects paymentDate in yyyy-MM-dd only; normalize from ISO or omit when empty
      const paymentDateValue = formData.paymentDate
        ? String(formData.paymentDate).split('T')[0]
        : undefined;
      const { paymentDate: _pd, ...restFormData } = formData;
      const submitData = {
        ...restFormData,
        baseSalary: Number(formData.baseSalary),
        bonuses: Number(formData.bonuses) || 0,
        deductions: Number(formData.deductions) || 0,
        overtime: Number(formData.overtime) || 0,
        tax: Number(formData.tax) || 0,
        netSalary: Number(formData.netSalary),
        workDays: Number(formData.workDays) || 22,
        ...(paymentDateValue ? { paymentDate: paymentDateValue } : {}),
      };

      if (isEdit && id) {
        await PayrollService.update(parseInt(id), submitData);
        setSuccess('Payroll updated successfully!');
      } else {
        await PayrollService.create(submitData);
        setSuccess('Payroll created successfully!');
      }

      setTimeout(() => {
        navigate('/payroll');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving payroll:', error);
      setError(
        error.response?.data?.error?.message ||
          'Failed to save payroll. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const grossSalary = Number(formData.baseSalary || 0) + Number(formData.bonuses || 0) + Number(formData.overtime || 0);

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEdit ? 'Edit Payroll' : 'New Payroll'}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {/* Employee and Period */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box flex={2}>
                  {/* Employee Selection */}
                  <Autocomplete
                    value={selectedEmployee}
                    onChange={handleEmployeeChange}
                    options={employees}
                    loading={employeesLoading}
                    getOptionLabel={(option) =>
                      `${option.attributes?.firstName} ${option.attributes?.lastName} (${option.attributes?.employeeNumber})`
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee *"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {employeesLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>

                <Box flex={1}>
                  {/* Period */}
                  <TextField
                    fullWidth
                    label="Period"
                    name="period"
                    type="month"
                    value={formData.period}
                    onChange={handleChange}
                    required
                    InputLabelProps={{ shrink: true }}
                    helperText="Select month and year"
                  />
                </Box>
              </Stack>

              {/* Employee Bank Account Info */}
              {selectedEmployee?.attributes?.employeeAccount && (
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Employee Bank Information
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {selectedEmployee.attributes.employeeBicCode && (
                      <Typography variant="body2">
                        <strong>BIC Code:</strong> {selectedEmployee.attributes.employeeBicCode}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>Account:</strong> {selectedEmployee.attributes.employeeAccount}
                    </Typography>
                    {selectedEmployee.attributes.salaryFrequency && (
                      <Typography variant="body2">
                        <strong>Frequency:</strong> {selectedEmployee.attributes.salaryFrequency === 'M' ? 'Monthly' : selectedEmployee.attributes.salaryFrequency}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {/* Currency */}
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                >
                  <MenuItem value="OMR">OMR - Omani Rial</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="SAR">SAR - Saudi Riyal</MenuItem>
                  <MenuItem value="AED">AED - UAE Dirham</MenuItem>
                </TextField>

                {/* Work Days */}
                <NumberTextField
                  fullWidth
                  label="Work Days"
                  name="workDays"
                  value={formData.workDays}
                  onChange={handleNumberFieldChange('workDays')}
                  inputProps={{ min: 1, max: 31 }}
                  allowDecimal={false}
                  helperText="Number of working days in the period"
                />
              </Stack>
            </Stack>
          </Paper>

          {/* employee salary */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              employee salary
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <NumberTextField
                  fullWidth
                  label="Base Salary"
                  name="baseSalary"
                  value={formData.baseSalary}
                  onChange={handleNumberFieldChange('baseSalary')}
                  required
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {formData.currency}
                      </InputAdornment>
                    ),
                  }}
                />

                <NumberTextField
                  fullWidth
                  label="Bonuses"
                  name="bonuses"
                  value={formData.bonuses}
                  onChange={handleNumberFieldChange('bonuses')}
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {formData.currency}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Performance bonuses, incentives, etc."
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <NumberTextField
                  fullWidth
                  label="Overtime Pay"
                  name="overtime"
                  value={formData.overtime}
                  onChange={handleNumberFieldChange('overtime')}
                  inputProps={{ step: '0.01', min: '0' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {formData.currency}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Extra hours compensation"
                />

                <Stack direction="row" spacing={1} flex={1}>
                  <NumberTextField
                    fullWidth
                    label="Tax"
                    name="tax"
                    value={formData.tax}
                    onChange={handleNumberFieldChange('tax')}
                    inputProps={{ step: '0.01', min: '0' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {formData.currency}
                        </InputAdornment>
                      ),
                    }}
                    helperText="Income tax deduction"
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleCalculateTax}
                    sx={{ minWidth: '120px', mt: '8px' }}
                    startIcon={<CalculateIcon />}
                  >
                    Calculate
                  </Button>
                </Stack>
              </Stack>

              <NumberTextField
                fullWidth
                label="Other Deductions"
                name="deductions"
                value={formData.deductions}
                onChange={handleNumberFieldChange('deductions')}
                inputProps={{ step: '0.01', min: '0' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.currency}
                    </InputAdornment>
                  ),
                }}
                helperText="Insurance, loans, advances, etc."
                sx={{ maxWidth: { sm: '50%' } }}
              />
            </Stack>

            {/* Calculation Summary */}
            <Box mt={4}>
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="space-around">
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">
                        Gross Salary
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {formData.currency} {grossSalary.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">
                        Total Deductions
                      </Typography>
                      <Typography variant="h6" color="error">
                        {formData.currency}{' '}
                        {(
                          Number(formData.deductions || 0) + Number(formData.tax || 0)
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="caption" color="text.secondary">
                        Net Salary
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        {formData.currency} {formData.netSalary.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Paper>

          {/* Payment Details */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="processed">Processed</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Payment Method"
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                >
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  label="Payment Date"
                  name="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Additional notes or comments..."
              />
            </Stack>
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/payroll')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : isEdit ? 'Update Payroll' : 'Create Payroll'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default PayrollForm;

